import { queryKeys } from '@repo/api-client/react';
import type { OrganizationListItem } from '@repo/api-client/dtos';
import type { QueryClient } from '@tanstack/react-query';

/**
 * After an org-list-modifying mutation patches the React Query cache,
 * mirror the result into the `SessionProvider`'s local `organizations`
 * projection so the active-organization guard, switcher, and any other
 * consumer of `useSession().organizations` agree with the cache on the
 * very next render.
 *
 * Without this, creating a workspace and immediately switching into it
 * snaps back to a previously-active org because the guard sees the new
 * id "isn't accessible" — the session list is the authoritative one
 * for the guard, and it hadn't seen the create yet. Same shape for
 * archive / restore / hard-delete / update.
 *
 * Pulled out of each individual mutation hook so the policy is applied
 * uniformly and any future tweak (e.g. dispatching to a side-channel
 * for analytics) only needs one edit.
 *
 * IMPORTANT — empty / missing cache handling:
 *   The React Query cache for the orgs list is only populated by
 *   surfaces that call `useOrganizations()` (dashboard / switcher /
 *   businesses settings). Lots of screens — the invoice preview,
 *   invoice detail, create / edit invoice — never observe that list,
 *   so the cache is genuinely empty there.
 *
 *   When that's the case, the wrapper mutations (e.g. `useUpdateOrganization`)
 *   call `setQueryData(...)` with an updater that returns `cache` if
 *   it was undefined — meaning the cache stays empty. We MUST NOT
 *   forward that empty value into the session projection: the session
 *   was independently populated from the auth bootstrap and is the
 *   source of truth that `useAuthRouting` reads to decide whether the
 *   user should be on `/create-business`.
 *
 *   Pushing `[]` here used to trigger a major bug: editing business
 *   info from `/invoice/preview/[id]` → Options → Business info would
 *   PATCH the org, this helper would push `[]` to the session, and the
 *   auth router would yank the user to "Set up studio" mid-edit. Skip
 *   the sync entirely in that case — the next full list fetch (next
 *   visit to dashboard / switcher) will rehydrate the cache with the
 *   already-persisted update on its own.
 */
export function syncOrgsFromCache(
  queryClient: QueryClient,
  syncOrganizations: (items: OrganizationListItem[]) => void,
): void {
  const cached = queryClient.getQueryData<{ items: OrganizationListItem[] }>(
    queryKeys.organizations.all(),
  );
  if (!cached) return;
  syncOrganizations(cached.items);
}
